(function () {
  var __pre = window.FQ_GOOGLE || {};

  /**
   * Token-bucket rate limiter used to throttle outbound HTTP calls.
   * - maxPerWindow: how many calls are allowed per time window
   * - windowMs: size of the time window in milliseconds
   * Returned object exposes `schedule(task)` which queues tasks and runs them
   * when tokens are available, spreading bursts over time.
   */
  function createRateLimiter(maxPerWindow, windowMs) {
    let tokens = maxPerWindow;
    const queue = [];
    let last = Date.now();

    function refill() {
      const now = Date.now();
      const elapsed = now - last;
      last = now;
      const add = Math.floor((elapsed / windowMs) * maxPerWindow);
      if (add > 0) tokens = Math.min(maxPerWindow, tokens + add);
    }

    async function schedule(task) {
      return new Promise((resolve, reject) => {
        queue.push({ task, resolve, reject });
        process();
      });
    }

    function process() {
      refill();
      while (tokens > 0 && queue.length) {
        tokens -= 1;
        const { task, resolve, reject } = queue.shift();
        Promise.resolve()
          .then(task)
          .then(resolve)
          .catch(reject)
          .finally(() => {
            setTimeout(process, Math.ceil(windowMs / maxPerWindow));
          });
      }
    }

    return { schedule };
  }

  // Note: SDK de Maps no es necesario para Places v1 REST; no esperamos a google.maps

  /**
   * Global, lightweight facade for Google services used by the theme.
   * Responsibilities:
   * - Places Autocomplete (New) v1 via REST
   * - Geocoding v3 (JS SDK if present, otherwise REST)
   * - Consistent rate limiting and logging
   * - Minimal helpers to normalize results for the theme
   */
  const FQ_GOOGLE = {
    // Populated by Liquid at runtime
    apiKey: __pre.apiKey || "",
    language:
      __pre.language ||
      (window.Shopify &&
        (window.Shopify.locale ||
          (window.Shopify.routes &&
            window.Shopify.routes.root &&
            document.documentElement.lang))) ||
      "en",
    region: __pre.region || (window.Shopify && window.Shopify.country) || "US",
    // Toggle to silence console debug when needed
    debug: true,
    // Shared token-bucket limiter for all outbound requests (~5 req/sec)
    limiter: createRateLimiter(5, 1000),

    /**
     * Debug logger with a consistent prefix.
     */
    log() {
      if (!this.debug) return;
      try {
        console.debug("[FQ][Google]", ...arguments);
      } catch (_) {}
    },

    /**
     * Builds a query string for classic REST endpoints (e.g. Geocoding v3),
     * automatically injecting key/language/region.
     */
    buildParams(params) {
      const defaulted = Object.assign(
        { key: this.apiKey, language: this.language, region: this.region },
        params || {}
      );
      const usp = new URLSearchParams();
      Object.keys(defaulted).forEach((k) => {
        if (
          defaulted[k] !== undefined &&
          defaulted[k] !== null &&
          defaulted[k] !== ""
        )
          usp.append(k, defaulted[k]);
      });
      return usp.toString();
    },

    /**
     * Places Autocomplete (New) v1 via REST.
     * Input: partial text typed by the user (+ optional sessionToken/country).
     * Output: normalized array of suggestions [{ description, place_id, terms }].
     * Rate limited by the shared limiter to avoid bursts.
     */
    async placesAutocomplete({ input, sessionToken, locationBias, country }) {
      if (!input || input.length < 3) return [];
      const body = {
        input: input,
        languageCode: this.language,
        sessionToken: sessionToken || this.uuid(),
        // Priorizar ciudades
        includedPrimaryTypes: ["locality"],
        regionCode: this.region,
      };
      if (country) body.includedRegionCodes = [String(country).toUpperCase()];
      // Nota: locationBias no está implementado aquí; se puede añadir con circleRectangularLocationBias si fuese necesario

      const run = async () => {
        const res = await fetch(
          "https://places.googleapis.com/v1/places:autocomplete",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": this.apiKey,
              "X-Goog-FieldMask":
                "suggestions.placePrediction.text,suggestions.placePrediction.placeId,suggestions.placePrediction.structuredFormat.secondaryText",
            },
            body: JSON.stringify(body),
          }
        );
        if (!res.ok) {
          this.log("Places v1 HTTP", res.status);
          return [];
        }
        const data = await res.json();

        const suggestions = Array.isArray(data.suggestions)
          ? data.suggestions
          : [];

        return suggestions.map((s) => {
          const pp = s.placePrediction || {};
          const text = (pp.text && pp.text.text) || "";
          const secondary =
            (pp.structuredFormat &&
              pp.structuredFormat.secondaryText &&
              pp.structuredFormat.secondaryText.text) ||
            "";
          // terms aproximados: primera parte ciudad, última parte país
          const parts = (text || "").split(",").map((p) => p.trim());
          let city = parts[0] || "";
          let countryName = secondary || parts[parts.length - 1] || "";
          return {
            description: countryName ? `${city}, ${countryName}` : text,
            place_id: pp.placeId || "",
            terms: [{ value: city }, { value: countryName }],
          };
        });
      };
      return this.limiter.schedule(run);
    },

    /**
     * Geocoding v3: resolves either by placeId or free-form address.
     * Prefers the JS SDK geocoder if present (no CORS), otherwise falls back
     * to the public REST JSON endpoint.
     */
    async geocode({ placeId, address }) {
      const run = async () => {
        if (window.google && google.maps && google.maps.Geocoder) {
          return new Promise((resolve) => {
            const geocoder = new google.maps.Geocoder();
            const req = placeId ? { placeId } : { address };
            geocoder.geocode(req, (results, status) => {
              if (
                status !== google.maps.GeocoderStatus.OK &&
                status !== google.maps.GeocoderStatus.ZERO_RESULTS
              ) {
                this.log("Geocode status:", status);
                return resolve({ results: [] });
              }
              resolve({ results: results || [] });
            });
          });
        }
        // Fallback REST
        const params = placeId ? { place_id: placeId } : { address };
        const url = `https://maps.googleapis.com/maps/api/geocode/json?${this.buildParams(
          Object.assign({}, params)
        )}`;
        const res = await fetch(url);
        if (!res.ok) {
          this.log("Geocode HTTP", res.status);
          return { results: [] };
        }
        const data = await res.json();
        return data;
      };
      return this.limiter.schedule(run);
    },

    /**
     * Extracts the minimal set of address components expected by the theme
     * from a single Geocoding result object.
     */
    extractComponents(geocodeResult) {
      const out = {
        city: "",
        postalCode: "",
        countryName: "",
        countryCode: "",
      };
      if (!geocodeResult) return out;
      const comps = geocodeResult.address_components || [];
      const get = (type) =>
        comps.find((c) => c.types && c.types.includes(type));
      const locality =
        get("locality") ||
        get("postal_town") ||
        get("administrative_area_level_2");
      const country = get("country");
      const postal = get("postal_code");
      if (locality) out.city = locality.long_name || locality.short_name || "";
      if (country) {
        out.countryName = country.long_name || "";
        out.countryCode = country.short_name || "";
      }
      if (postal) out.postalCode = postal.long_name || postal.short_name || "";
      return out;
    },

    /**
     * Runs geocode and immediately extracts components.
     * Returns parsed components plus the raw top result for advanced callers.
     */
    async resolveComponents({ placeId, address }) {
      try {
        const geo = await this.geocode({ placeId, address });
        const best = (geo && geo.results && geo.results[0]) || null;
        const parsed = this.extractComponents(best);
        return Object.assign({ rawResult: best }, parsed);
      } catch (_) {
        return { city: '', postalCode: '', countryName: '', countryCode: '', rawResult: null };
      }
    },

    /**
     * Lightweight UUID v4-like generator for session tokens (no crypto).
     */
    uuid() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0,
            v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },
  };

  window.FQ_GOOGLE = FQ_GOOGLE;
})();